import { element, slot } from "./dom.js";

/**
 * The search box a worksheet's List is chosen in.
 *
 * There are hundreds of Lists and their parts, which a `<select>` shows as
 * one long scroll. This is a combobox in the WAI-ARIA pattern instead: type
 * part of a List's English title, its Chinese title or its pinyin, and the
 * options narrow to the ones that match. Web Awesome Pro has one, as
 * `<wa-combobox>`, and this is drawn to look like it. The site uses no web
 * components, so a page draws without waiting for one to upgrade.
 *
 * **The `<select>` stays, hidden, and holds the choice.** Hugo writes every
 * List into it, so nothing is fetched, and `sheet-tool.ts` reads and writes
 * its value as it always has. Choosing an option here sets the select and
 * fires its `change`, which is what the sheet listens for. The box itself
 * builds its options from the select the first time it opens.
 *
 * A disabled option is a List too large for one sheet. It is listed as the
 * heading of its parts and cannot be chosen.
 */

/** One List as the box lists it. */
interface Choice {
  readonly value: string;
  readonly title: string;
  readonly hanzi: string;
  readonly part: boolean;
  readonly disabled: boolean;

  /** What a query is matched against, folded the way a query is. */
  readonly haystack: string;

  /** The List this is a part of, for a part. */
  readonly parent: Choice | undefined;

  readonly item: HTMLLIElement;
}

/** One group of Lists, such as HSK vocabulary or Topics. */
interface Group {
  readonly heading: HTMLLIElement;
  readonly choices: readonly Choice[];
}

/**
 * Text folded for matching: lower case, tone marks and punctuation gone, and
 * no spaces, so `tiān qì`, `tianqi` and `Tianqi` all match.
 */
export function folded(text: string): string {
  return text
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/[\s\p{P}]+/gu, "");
}

/**
 * The terms of a query, each of which a List has to match.
 *
 * A number joins the word before it, so `hsk 3` is one term and does not
 * also match part 3 of HSK 1.
 */
export function queryTerms(query: string): readonly string[] {
  return query
    .toLowerCase()
    .replace(/([a-z])\s+(\d)/gu, "$1$2")
    .split(/\s+/u)
    .map(folded)
    .filter((term) => term !== "");
}

/** What the box needs from the page. */
export interface ListPickerProperties {
  readonly input: HTMLInputElement;
  readonly listbox: HTMLElement;
  readonly select: HTMLSelectElement;
}

/** A combobox over the hidden `<select>` of Lists. */
export class ListPicker {
  readonly #input: HTMLInputElement;
  readonly #listbox: HTMLElement;
  readonly #select: HTMLSelectElement;
  #groups: readonly Group[] | undefined;
  #empty: HTMLLIElement | undefined;
  #shown: readonly Choice[] = [];
  #active = -1;

  constructor(properties: ListPickerProperties) {
    this.#input = properties.input;
    this.#listbox = properties.listbox;
    this.#select = properties.select;

    this.#input.addEventListener("focus", () => {
      this.#input.select();
      this.#open();
    });
    this.#input.addEventListener("click", () => {
      this.#open();
    });
    this.#input.addEventListener("input", () => {
      this.#open();
    });
    // Leaving the box after typing fires its own `change`, which is no
    // choice. Only the select's, from `#choose`, should reach the sheet.
    this.#input.addEventListener("change", (event) => {
      event.stopPropagation();
    });
    this.#input.addEventListener("keydown", (event) => {
      this.#key(event);
    });
    this.#input.addEventListener("blur", () => {
      this.#close();
    });
    // A press on an option would blur the box before the click lands.
    this.#listbox.addEventListener("mousedown", (event) => {
      event.preventDefault();
    });
    this.#listbox.addEventListener("click", (event) => {
      const item = (event.target as Element | null)?.closest("li");
      const choice = this.#shown.find((each) => each.item === item);
      if (choice !== undefined) {
        this.#choose(choice);
      }
    });
    this.sync();
  }

  /** The picker in a generator's container, if the page has one. */
  static on(container: HTMLElement): ListPicker | undefined {
    const picker = slot(container, "list-picker");
    const input = picker?.querySelector("input");
    const listbox = picker?.querySelector<HTMLElement>("[role=listbox]");
    const select = slot(container, "list");

    return input instanceof HTMLInputElement &&
      listbox !== null &&
      listbox !== undefined &&
      select instanceof HTMLSelectElement
      ? new ListPicker({ input, listbox, select })
      : undefined;
  }

  /** Show the chosen List's title in the box, after the select was set. */
  sync(): void {
    this.#input.value = this.#chosenTitle();
  }

  #open(): void {
    const groups = this.#build();
    const terms = queryTerms(
      this.#input.value === this.#chosenTitle() ? "" : this.#input.value,
    );
    const matches = (choice: Choice): boolean =>
      terms.every((term) => choice.haystack.includes(term));
    const shown: Choice[] = [];

    for (const group of groups) {
      let any = false;
      for (const choice of group.choices) {
        // A List shows with its parts, and a part with its List.
        const visible =
          matches(choice) ||
          (choice.parent !== undefined && matches(choice.parent)) ||
          group.choices.some(
            (other) => other.parent === choice && matches(other),
          );
        choice.item.hidden = !visible;
        if (visible) {
          any = true;
          shown.push(choice);
        }
      }
      group.heading.hidden = !any;
    }

    this.#shown = shown;
    this.#empty!.hidden = shown.length > 0;
    this.#listbox.hidden = false;
    this.#input.setAttribute("aria-expanded", "true");
    const chosen = shown.findIndex(
      (choice) => choice.value === this.#select.value,
    );
    this.#activate(
      chosen >= 0 && terms.length === 0 ? chosen : this.#next(-1, 1),
    );
  }

  #close(): void {
    this.#listbox.hidden = true;
    this.#input.setAttribute("aria-expanded", "false");
    this.#input.removeAttribute("aria-activedescendant");
    this.#active = -1;
    this.sync();
  }

  #key(event: KeyboardEvent): void {
    const open = this.#listbox.hidden !== true;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!open) {
        this.#open();

        return;
      }
      this.#activate(
        this.#next(this.#active, event.key === "ArrowDown" ? 1 : -1),
      );
    } else if (event.key === "Enter" && open) {
      event.preventDefault();
      const choice = this.#shown[this.#active];
      if (choice !== undefined) {
        this.#choose(choice);
      }
    } else if (event.key === "Escape" && open) {
      event.preventDefault();
      this.#close();
    }
  }

  /** The next option that can be chosen, from one place, either way. */
  #next(from: number, step: 1 | -1): number {
    for (let at = from + step; at >= 0 && at < this.#shown.length; at += step) {
      if (!this.#shown[at]!.disabled) {
        return at;
      }
    }

    return from;
  }

  #activate(at: number): void {
    this.#shown[this.#active]?.item.classList.remove("cb-ws-picker-active");
    this.#active = at;
    const choice = this.#shown[at];
    if (choice === undefined) {
      this.#input.removeAttribute("aria-activedescendant");

      return;
    }
    choice.item.classList.add("cb-ws-picker-active");
    this.#input.setAttribute("aria-activedescendant", choice.item.id);
    choice.item.scrollIntoView?.({ block: "nearest" });
  }

  #choose(choice: Choice): void {
    if (choice.disabled) {
      return;
    }
    this.#select.value = choice.value;
    for (const shown of this.#shown) {
      shown.item.setAttribute(
        "aria-selected",
        shown === choice ? "true" : "false",
      );
    }
    this.#select.dispatchEvent(new Event("change", { bubbles: true }));
    this.#close();
  }

  #chosenTitle(): string {
    const chosen = this.#select.selectedOptions[0];

    return chosen === undefined || chosen.value === ""
      ? ""
      : chosen.text.trim();
  }

  /** The options, drawn from the select the first time the box opens. */
  #build(): readonly Group[] {
    if (this.#groups !== undefined) {
      return this.#groups;
    }
    const groups: Group[] = [];
    let index = 0;

    for (const optgroup of this.#select.querySelectorAll("optgroup")) {
      const heading = element("li", {
        className: "cb-ws-picker-group",
        text: optgroup.label,
        attributes: { role: "presentation" },
      });
      this.#listbox.append(heading);
      const choices: Choice[] = [];
      let parent: Choice | undefined;

      for (const option of optgroup.querySelectorAll("option")) {
        const part = option.hasAttribute("data-cb-part");
        const title = option.text.trim();
        const hanzi = option.dataset["cbHanzi"] ?? "";
        const pinyin = option.dataset["cbPinyin"] ?? "";
        const selected = option.value === this.#select.value;
        const item = element("li", {
          className: `cb-ws-picker-option${part ? " cb-ws-picker-part" : ""}`,
          attributes: {
            id: `cb-ws-list-option-${String((index += 1))}`,
            role: "option",
            "aria-selected": selected ? "true" : "false",
            "aria-disabled": option.disabled ? "true" : undefined,
          },
          children: [
            element("span", {
              className: "cb-ws-picker-title",
              text: title,
            }),
            element("span", {
              className: "cb-ws-picker-hanzi",
              text: hanzi,
              attributes: { lang: "zh-Hans" },
            }),
            ...(option.disabled
              ? [
                  element("span", {
                    className: "cb-ws-picker-note",
                    text: "Choose a part below",
                  }),
                ]
              : []),
          ],
        });
        this.#listbox.append(item);
        const choice: Choice = {
          value: option.value,
          title,
          hanzi,
          part,
          disabled: option.disabled,
          haystack: folded(`${title} ${hanzi} ${pinyin}`),
          parent: part ? parent : undefined,
          item,
        };
        if (!part) {
          parent = choice;
        }
        choices.push(choice);
      }
      groups.push({ heading, choices });
    }

    this.#empty = element("li", {
      className: "cb-ws-picker-empty",
      text: "No List matches.",
      attributes: { role: "presentation" },
    });
    this.#listbox.append(this.#empty);
    this.#groups = groups;

    return groups;
  }
}
